import {
  ArrowClockwiseIcon,
  CheckIcon,
  SparkleIcon,
  SpinnerGapIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Badge, Button } from '@admin-panel/ui';
import { LookDTOType } from '../../../../../schemas/styling';

import { getModelTagConfig } from '../helpers';

interface WorkflowRunItemHeaderProps {
  currentLook: LookDTOType;
  isImageGenerating: boolean;
  isPollingLook: boolean;
  canApprove: boolean;
  canReject: boolean;
  isReRunning: boolean;
  isPublishing: boolean;
  isUnpublishing: boolean;
  isRejecting: boolean;
  onReRun: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onReject: () => void;
}

export const WorkflowRunItemHeader = ({
  currentLook,
  isImageGenerating,
  isPollingLook,
  canApprove,
  canReject,
  isReRunning,
  isPublishing,
  isUnpublishing,
  isRejecting,
  onReRun,
  onPublish,
  onUnpublish,
  onReject,
}: WorkflowRunItemHeaderProps) => {
  const modelTag = getModelTagConfig(currentLook.image_generation_model);

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0 flex-1 overflow-x-auto">
        <div className="flex min-w-max items-center gap-1.5">
          <Badge size="medium">Look #{currentLook.id}</Badge>
          <Badge size="medium">Product {currentLook.base_product_type}</Badge>
          {currentLook.workflow_genre ? (
            <Badge size="medium" tone="accent">
              {currentLook.workflow_genre}
            </Badge>
          ) : null}
          {modelTag ? (
            <Badge size="medium" tone={modelTag.tone}>
              {modelTag.withSparkle ? <SparkleIcon size={12} /> : null}
              {modelTag.label}
            </Badge>
          ) : null}
          {isImageGenerating && (
            <Badge size="medium" tone="info">
              <SpinnerGapIcon className="animate-spin" size={12} />
              {currentLook.image_generation_status === 'pending'
                ? 'Image pending'
                : 'Image generating'}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {!isImageGenerating && currentLook.qc_status === 'pending' && (
          <Button
            size="small"
            variant="outline"
            onClick={onReRun}
            isDisabled={isReRunning || isPollingLook}
            isLoading={isReRunning || isPollingLook}
            icon={<ArrowClockwiseIcon size={14} />}
            type="button"
          >
            {isPollingLook ? 'Re-running...' : 'Re-run'}
          </Button>
        )}

        {(canApprove || canReject) &&
          !isImageGenerating &&
          currentLook.image_generation_status !== 'failed' && (
            <>
              {canApprove && (
                <Button
                  size="small"
                  tone={currentLook.qc_status === 'published' ? 'warning' : 'primary'}
                  variant={currentLook.qc_status === 'published' ? 'outline' : 'solid'}
                  onClick={currentLook.qc_status === 'published' ? onUnpublish : onPublish}
                  isDisabled={isPublishing || isUnpublishing}
                  isLoading={isPublishing || isUnpublishing}
                  icon={<CheckIcon size={14} />}
                  type="button"
                >
                  {currentLook.qc_status === 'published' ? 'Unpublish' : 'Publish'}
                </Button>
              )}
              {canReject && (
                <Button
                  tone="destructive"
                  variant="subtle"
                  size="small"
                  onClick={onReject}
                  isDisabled={isRejecting || currentLook.qc_status === 'rejected'}
                  isLoading={isRejecting}
                  icon={<XCircleIcon size={14} />}
                  type="button"
                >
                  Reject
                </Button>
              )}
            </>
          )}
      </div>
    </div>
  );
};
